import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppState } from '~/reducers';
import {
  cleanupExpiredPayloads,
  getEarliestExpiryTimestamp,
} from '~/domain/assets/store/hfs-operations/reducer';

/*
 * This hook is responsible for retention of the expired payloads of hfs operations.
 * E.g. when operation has been submitted for execution, change was remembered in reducer (to be considered as effective across app) for some time, and then expired.
 * Expired state change assumption needs to be cleaned up.
 * By the expiry time, typically, change needs to be either confirmed (e.g. by WS), or treated as such that did not and will not take effect.
 * Currently covers MOWER_AREA_CUTTING_HEIGHT & MOWER_AREA_SCHEDULE.
 * */
const useHfsOperationsPayloadsRetention = () => {
  const dispatch = useDispatch();
  const operationsState = useSelector((state: AppState) => state.assetHfsOperations);
  const minTimestamp = useMemo(
    () => getEarliestExpiryTimestamp(operationsState),
    [operationsState]
  );
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (minTimestamp !== undefined) {
      const diff = minTimestamp - Date.now();
      if (diff > 0) {
        timeoutRef.current = setTimeout(() => dispatch(cleanupExpiredPayloads()), diff);
      } else {
        dispatch(cleanupExpiredPayloads());
      }
    }
  }, [dispatch, minTimestamp]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [timeoutRef]);
};

export { useHfsOperationsPayloadsRetention };
